import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ivrTrees } from '@/api/ivrTrees';
import { cn } from '@/lib/cn';
import {
  Network, Voicemail, Clock, Plus, ChevronRight,
} from 'lucide-react';

// ── Template definitions (mirrors backend) ────────────────────────────────────

const TEMPLATES = [
  {
    key: 'main_menu_3',
    label: 'Main Menu',
    subtitle: '3 options',
    description: 'A welcome greeting with DTMF choices. Press 1, 2, or 3 to navigate.',
    icon: Network,
    accentClass: 'border-violet-200 hover:border-violet-300',
    iconClass: 'text-violet-500 bg-violet-50',
    nodeCount: 4,
    edgeCount: 4,
  },
  {
    key: 'voicemail_greeting',
    label: 'Voicemail Greeting',
    subtitle: 'greeting + mailbox',
    description: 'A simple greeting message followed by a voicemail recording box.',
    icon: Voicemail,
    accentClass: 'border-teal-200 hover:border-teal-300',
    iconClass: 'text-teal-500 bg-teal-50',
    nodeCount: 2,
    edgeCount: 1,
  },
  {
    key: 'business_hours',
    label: 'Business Hours',
    subtitle: 'after-hours menu',
    description: 'After-hours menu offering voicemail or emergency transfer.',
    icon: Clock,
    accentClass: 'border-amber-200 hover:border-amber-300',
    iconClass: 'text-amber-500 bg-amber-50',
    nodeCount: 3,
    edgeCount: 2,
  },
] as const;

interface Props {
  treeId: string;
  onApplied: () => void;
  onAddNode: () => void;
}

export function TemplateEmptyState({ treeId, onApplied, onAddNode }: Props) {
  const qc = useQueryClient();

  const applyTemplate = useMutation({
    mutationFn: (key: string) => ivrTrees.applyTemplate(treeId, key),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrTree', treeId] });
      onApplied();
    },
  });

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-8 px-8 py-12">
      {/* Heading */}
      <div className="text-center space-y-2">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
          <Network className="h-7 w-7 text-slate-300" />
        </div>
        <h3 className="text-base font-semibold text-slate-700">No nodes yet</h3>
        <p className="text-sm text-slate-400 max-w-xs">
          Start building your IVR tree. Add a node manually or choose a template to get started quickly.
        </p>
      </div>

      {/* Templates */}
      <div className="w-full max-w-2xl space-y-3">
        <p className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Start from a template
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {TEMPLATES.map((tpl) => {
            const Icon = tpl.icon;
            return (
              <button
                key={tpl.key}
                type="button"
                onClick={() => applyTemplate.mutate(tpl.key)}
                disabled={applyTemplate.isPending}
                className={cn(
                  'group relative flex flex-col items-start gap-2.5 rounded-xl border bg-white p-4',
                  'text-left transition-all duration-150 hover:shadow-md active:scale-[0.98]',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  tpl.accentClass,
                )}
              >
                {/* Icon */}
                <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg', tpl.iconClass)}>
                  <Icon className="h-4.5 w-4.5" />
                </div>

                {/* Labels */}
                <div>
                  <p className="text-sm font-semibold text-slate-800 leading-tight">{tpl.label}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{tpl.subtitle}</p>
                </div>

                <p className="text-[11px] text-slate-500 leading-relaxed">{tpl.description}</p>

                {/* Node count */}
                <div className="flex items-center gap-2 mt-auto pt-1">
                  <span className="text-[10px] text-slate-400">
                    {tpl.nodeCount} nodes · {tpl.edgeCount} edges
                  </span>
                </div>

                {/* Arrow on hover */}
                <ChevronRight
                  className={cn(
                    'absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-200',
                    'transition-all duration-150 group-hover:translate-x-0.5 group-hover:text-slate-400',
                  )}
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* Divider */}
      <div className="flex w-full max-w-2xl items-center gap-3">
        <div className="flex-1 h-px bg-slate-100" />
        <span className="text-[11px] text-slate-400">or</span>
        <div className="flex-1 h-px bg-slate-100" />
      </div>

      {/* Manual add */}
      <button
        type="button"
        onClick={onAddNode}
        className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-5 py-3 text-sm font-medium text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-700"
      >
        <Plus className="h-4 w-4" />
        Add first node manually
      </button>

      {/* Applying indicator */}
      {applyTemplate.isPending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60">
          <div className="rounded-xl border border-slate-200 bg-white px-8 py-6 shadow-xl text-center space-y-2">
            <div className="animate-spin h-8 w-8 border-2 border-indigo-400 border-t-transparent rounded-full mx-auto" />
            <p className="text-sm font-medium text-slate-700">Applying template…</p>
          </div>
        </div>
      )}
    </div>
  );
}
